import { useMutation } from '@apollo/client';
import type { InsertTaxonomyMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertTaxonomyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertTaxonomyTRPC } from './useInsertTaxonomyTRPC';

/**
 * The seed values the legacy `InsertTaxonomy` mutation hard-coded in its
 * `.graphql` (the insert had no form). The tRPC path now sends them as
 * variables; the Apollo path still relies on the document's literals, so this
 * default is only forwarded to the tRPC branch.
 */
const DEFAULT_TAXONOMY_INSERT = {
  Description: 'Description',
  Common: {},
  Library: {},
  Rating: {},
  Taxonomy: {},
  InternalAuditRating: {},
  OversightRating: {},
  Locale: 'en',
  OrgName: '',
} as const;

/**
 * Flag-gated wrapper for the compound `InsertTaxonomy` mutation (RSP-6488).
 * Defaults to the legacy Apollo mutation and delegates to the tRPC twin when
 * the `trpc` flag is on. Consumers import THIS hook, never the `*TRPC` variant.
 */
export const useInsertTaxonomy = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(InsertTaxonomyDocument, {
    update: (cache) => {
      evictField(cache, 'taxonomy');
      evictField(cache, 'taxonomy_org');
      evictField(cache, 'taxonomy_audit');
    },
  });

  const trpcMutation = useInsertTaxonomyTRPC();

  const insertTaxonomy = async (): Promise<InsertTaxonomyMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertTaxonomy(DEFAULT_TAXONOMY_INSERT);
    }

    const result = await insertGraphQL();
    if (!result.data) {
      throw new Error('Failed to create taxonomy');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertTaxonomy,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertTaxonomy,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
